import { normalizePlate, plateText, foldLookalikes, foldLeadingZeros } from './normalize';

export type Comparison = 'same' | 'format-variant' | 'lookalike' | 'near' | 'contained' | 'different' | 'blank';

/** Longest normalized plate value that is ever compared. Real serials are far shorter; longer input is junk or abuse. */
export const MAX_PLATE_VALUE_LEN = 64;
/** 'near' (a probable typo) needs at least this many characters on both sides; on shorter values 1-2 edits is a different value. */
const MIN_NEAR_LEN = 5;
/** 'contained' needs the shorter value to have at least this many characters: "CAT" in "CATERPILLAR" counts, "12" in a serial does not. */
const MIN_CONTAINED_LEN = 3;
/** A fragment with ONE swapped pair of neighbouring characters ("160386" for "...160368") is still a fragment from this many characters up. */
const MIN_MISREAD_FRAGMENT_LEN = 6;
/**
 * The digit boundary protects only ALL-DIGIT values SHORTER than this: "100" vs "1000" is another size. A fragment this long that
 * ends inside a digit run ("3376GMLL0" for "3376GMLL0005"), or a shorter one with a letter in it ("4JK00" for "4JK001158"), is a
 * cut-off read, and approving it would truncate the record.
 */
const MIN_RUN_CUT_LEN = 6;

/** `a` and `b` (same length) differ only by ONE swap of two neighbouring characters. */
function oneAdjacentSwap(a: string, b: string): boolean {
  let i = 0;
  while (i < a.length && a[i] === b[i]) i++;
  return i + 1 < a.length && a[i] === b[i + 1] && a[i + 1] === b[i] && a.slice(i + 2) === b.slice(i + 2);
}

/**
 * Optimal string alignment distance (Levenshtein + adjacent transposition) using three rolling rows.
 * Stops as soon as no row can come back under `maxDist` and then returns `maxDist + 1`; a result <= maxDist is exact.
 */
export function editDistance(a: string, b: string, maxDist = Infinity): number {
  let older: number[] = [];
  let prev: number[] = Array.from({ length: b.length + 1 }, (_, j) => j);
  let prevMin = 0;
  for (let i = 1; i <= a.length; i++) {
    const cur: number[] = [i];
    let curMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let d = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d = Math.min(d, older[j - 2] + 1);
      cur[j] = d;
      if (d < curMin) curMin = d;
    }
    // A transposition reaches back two rows, so both of the last two rows must be out of range to stop.
    if (curMin > maxDist && prevMin > maxDist) return maxDist + 1;
    older = prev;
    prev = cur;
    prevMin = curMin;
  }
  return prev[b.length];
}

/** Compare two raw values. Order matters only for 'near' highlighting, not the result. */
export function comparePlateValues(rawA: string | null, rawB: string | null): Comparison {
  const a = normalizePlate(rawA);
  const b = normalizePlate(rawB);
  if (!a || !b) return 'blank';
  if (a.length > MAX_PLATE_VALUE_LEN || b.length > MAX_PLATE_VALUE_LEN) return 'different';
  if (a === b) return 'same';
  if (foldLeadingZeros(a) === foldLeadingZeros(b)) return 'format-variant';
  if (foldLookalikes(a) === foldLookalikes(b)) return 'lookalike';
  if (isNear(a, b)) return 'near';
  const contained = a.length <= b.length ? isStrictPart(rawA, rawB) : isStrictPart(rawB, rawA);
  return contained ? 'contained' : 'different';
}

/** A probable typo: 1-2 edits, scaled by length, on values of at least MIN_NEAR_LEN characters. */
function isNear(a: string, b: string): boolean {
  if (a.length < MIN_NEAR_LEN || b.length < MIN_NEAR_LEN) return false;
  const maxNear = Math.max(1, Math.min(2, Math.floor(Math.max(a.length, b.length) / 5)));
  if (Math.abs(a.length - b.length) > maxNear) return false;
  return editDistance(a, b, maxNear) <= maxNear;
}

const isDigit = (c: string | undefined): boolean => c !== undefined && c >= '0' && c <= '9';

/** A normalized character and whether a token edge (a space, dash, slash...) stood right before it in the value. */
interface PlateChar { c: string; sepBefore: boolean }

/** A single '.' or ',' between two digits is part of the number ("125.7", "100,000"), not a token edge. */
const IN_NUMBER = /^[.,]$/;

function plateChars(raw: string | null): PlateChar[] {
  const out: PlateChar[] = [];
  let sep = '';
  for (const ch of plateText(raw)) {
    if (/[A-Z0-9]/.test(ch)) {
      const inNumber = IN_NUMBER.test(sep) && isDigit(out[out.length - 1]?.c) && isDigit(ch);
      out.push({ c: ch, sepBefore: sep !== '' && out.length > 0 && !inNumber });
      sep = '';
    } else sep += ch;
  }
  return out;
}

/**
 * Does the fragment at [start, start + len) of `long` split a digit run of the longer value? Decided on TOKEN edges of the value as
 * written: a separator is never a digit ("SERIES 60" in "Series 60 12.7L"), and zeros that open a digit run are not more digits
 * ("17759" in "8.8L0017759"). Read on the unfolded characters: a lookalike letter next to the fragment is NOT a digit (named limit:
 * "500" in "L500" is a fragment, the soft direction).
 */
function splitsDigitRun(frag: string, long: readonly PlateChar[], start: number, len: number): boolean {
  const end = start + len;
  if (isDigit(frag[frag.length - 1]) && end < long.length && isDigit(long[end].c) && !long[end].sepBefore) return true;
  if (!isDigit(frag[0]) || start === 0 || long[start].sepBefore || !isDigit(long[start - 1].c)) return false;
  // Walk back over the digit run the fragment would continue: only opening zeros are allowed there.
  for (let j = start - 1; j >= 0 && isDigit(long[j].c); j--) {
    if (long[j].c !== '0') return true;
    if (long[j].sepBefore) break;
  }
  return false;
}

/**
 * Is `rawPart` a STRICT part of `rawWhole` ("3512" in "CAT 3512B", "CAT" in "CATERPILLAR")? THE one containment rule, used by the
 * comparator ('contained'), the verdict (a plate value that is part of the record is never a one-click "use plate") and the proposal
 * writer (never queue a value that would cut the record short), so they cannot drift.
 *  - at least MIN_CONTAINED_LEN characters after its own leading zeros, and strictly shorter than the whole;
 *  - matched with lookalikes folded on both sides (O/0, I/1, S/5, ...), so "16O368" is part of "PE5030T160368";
 *  - an ALL-DIGIT value under MIN_RUN_CUT_LEN never splits a digit run ("100" is not part of "1000": a 100 kW vs a 1000 kW machine),
 *    decided on token edges (splitsDigitRun); a longer fragment, or one with a letter, that ends inside a digit run is a cut-off read
 *    and IS a part;
 *  - from MIN_MISREAD_FRAGMENT_LEN characters up, ONE swap of neighbouring characters is allowed ("160386"). A substituted character
 *    is NOT: "QSX15-G9" vs "QSX15-G8" is a real variant. Two swaps, an inserted or dropped character, or a shorter fragment are not
 *    caught (named limit).
 */
export function isStrictPart(rawPart: string | null, rawWhole: string | null): boolean {
  const part = normalizePlate(rawPart).replace(/^0+(?=\d)/, '');
  const long = plateChars(rawWhole);
  const len = part.length;
  if (len < MIN_CONTAINED_LEN || len >= long.length || long.length > MAX_PLATE_VALUE_LEN) return false;
  const p = foldLookalikes(part);
  const w = foldLookalikes(long.map((x) => x.c).join(''));
  const swapAllowed = len >= MIN_MISREAD_FRAGMENT_LEN;
  const digitRule = len < MIN_RUN_CUT_LEN && /^\d+$/.test(part);
  for (let i = 0; i + len <= w.length; i++) {
    const window = w.slice(i, i + len);
    if ((window === p || (swapAllowed && oneAdjacentSwap(p, window))) && !(digitRule && splitsDigitRun(part, long, i, len))) return true;
  }
  return false;
}

/**
 * Would approving the plate value CUT THE RECORD SHORT? True when the plate value is a strict part of the record (isStrictPart), which
 * covers a 1-2 character truncation too ("4JK00115" for "4JK001158": 6+ characters, so a cut digit run is a cut-off read). A short
 * number inside a longer one ("15000" for "150000") is a real disagreement, never this. Directional: a record that is part of the
 * plate value is a fill-in, not this.
 */
export function plateTruncatesRecord(rawPlate: string | null, rawRecord: string | null): boolean {
  return normalizePlate(rawPlate).length < normalizePlate(rawRecord).length && isStrictPart(rawPlate, rawRecord);
}

/**
 * A conflict a person must decide ONE ROW AT A TIME, never batch-approved: one value is part of the other by the same rule
 * (isStrictPart, either way: a label or displacement glued on, zero-stripped or swapped), or one plainly contains the other
 * (lookalikes folded: a short number inside a longer one, "100" vs "1000"), or the plate value is shorter than the record at all
 * (any approval that shortens the saved value: a dropped character, "PE5030T16368").
 */
export function singleReviewOnly(rawPlate: string | null, rawRecord: string | null): boolean {
  const p = normalizePlate(rawPlate);
  const r = normalizePlate(rawRecord);
  if (!p || !r || p === r) return false;
  const fp = foldLookalikes(p);
  const fr = foldLookalikes(r);
  return p.length < r.length || fp.includes(fr) || fr.includes(fp) || isStrictPart(rawRecord, rawPlate) || isStrictPart(rawPlate, rawRecord);
}

/**
 * Indexes in the NORMALIZED `a` (not the raw text) whose character differs from normalized `b` at the same index.
 * Simple, for highlighting. Empty when either side is over the comparison cap.
 */
export function diffPositions(rawA: string | null, rawB: string | null): number[] {
  const a = normalizePlate(rawA);
  const b = normalizePlate(rawB);
  if (a.length > MAX_PLATE_VALUE_LEN || b.length > MAX_PLATE_VALUE_LEN) return [];
  const out: number[] = [];
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) out.push(i);
  return out;
}
